<script lang="ts">
	import { onMount } from 'svelte';
	import { tagManager } from '$lib/docs/tags';
	import type { Language } from '$lib/docs/types';
	
	interface Props {
		currentSlug: string;
		tags: string[];
		locale?: Language;
		limit?: number;
	}
	
	let { currentSlug, tags, locale = 'en', limit = 4 }: Props = $props();
	
	interface RelatedArticle {
		slug: string;
		title: string;
		description: string;
		matchingTags: string[];
		score: number;
	}
	
	let relatedArticles = $state<RelatedArticle[]>([]);
	let loading = $state(true);
	
	onMount(async () => {
		await loadRelatedArticles();
	});
	
	async function loadRelatedArticles() {
		if (!tags || tags.length === 0) {
			loading = false;
			return;
		}
		
		loading = true;
		const articlesMap = new Map<string, RelatedArticle>();
		
		try {
			// Get articles for each tag
			for (const tag of tags) {
				const tagInfo = await tagManager.getTagInfo(tag, locale);
				if (!tagInfo) continue;
				
				for (const article of tagInfo.articles) {
					// Skip the current article
					if (article.slug === currentSlug) continue;
					
					if (!articlesMap.has(article.slug)) {
						articlesMap.set(article.slug, {
							slug: article.slug,
							title: article.title,
							description: article.description,
							matchingTags: [tag],
							score: 1
						});
					} else {
						const existing = articlesMap.get(article.slug)!;
						existing.matchingTags.push(tag);
						existing.score++;
					}
				}
			}
			
			// Sort by score (number of matching tags) and limit
			relatedArticles = Array.from(articlesMap.values())
				.sort((a, b) => b.score - a.score)
				.slice(0, limit);
				
		} catch (error) {
			console.error('Error loading related articles:', error);
		} finally {
			loading = false;
		}
	}
</script>

{#if !loading && relatedArticles.length > 0}
	<div class="related-articles">
		<h3 class="related-articles__title">Related Articles</h3>
		<div class="related-articles__list">
			{#each relatedArticles as article}
				<a href="/docs/{article.slug}" class="related-article">
					<div class="related-article__content">
						<h4 class="related-article__title">{article.title}</h4>
						<p class="related-article__description">{article.description}</p>
						<div class="related-article__tags">
							{#each article.matchingTags as tag}
								<span class="related-article__tag">#{tag}</span>
							{/each}
						</div>
					</div>
					<svg class="related-article__arrow" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
						<path d="M6.22 3.22a.75.75 0 011.06 0l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 010-1.06z"/>
					</svg>
				</a>
			{/each}
		</div>
	</div>
{/if}

<style>
	.related-articles {
		margin-top: 3rem;
		padding-top: 2rem;
		border-top: 1px solid var(--border-light);
	}
	
	.related-articles__title {
		font-size: 1rem;
		font-weight: 600;
		color: var(--text-primary);
		margin: 0 0 1rem 0;
	}
	
	.related-articles__list {
		display: grid;
		gap: 0.75rem;
	}
	
	.related-article {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 0.875rem;
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: 6px;
		text-decoration: none;
		transition: all 0.2s ease;
	}
	
	.related-article:hover {
		background: var(--bg-tertiary);
		border-color: var(--brand-primary);
		transform: translateX(2px);
	}
	
	.related-article__content {
		flex: 1;
		min-width: 0;
	}
	
	.related-article__title {
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--text-primary);
		margin: 0 0 0.25rem 0;
		line-height: 1.4;
	}
	
	.related-article__description {
		font-size: 0.75rem;
		color: var(--text-tertiary);
		margin: 0 0 0.375rem 0;
		line-height: 1.4;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}
	
	.related-article__tags {
		display: flex;
		gap: 0.375rem;
		flex-wrap: wrap;
	}
	
	.related-article__tag {
		display: inline-block;
		padding: 0.125rem 0.375rem;
		background: var(--bg-primary);
		color: var(--text-tertiary);
		font-size: 0.75rem;
		border-radius: 3px;
		font-weight: 500;
	}
	
	.related-article__arrow {
		color: var(--text-tertiary);
		flex-shrink: 0;
		transition: all 0.2s ease;
		opacity: 0;
	}
	
	.related-article:hover .related-article__arrow {
		opacity: 1;
		color: var(--brand-primary);
	}
	
	/* Dark mode */
	:global([data-theme='dark']) .related-articles {
		border-top-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .related-article {
		background: rgba(255, 255, 255, 0.02);
		border-color: rgba(255, 255, 255, 0.08);
	}
	
	:global([data-theme='dark']) .related-article:hover {
		background: rgba(255, 255, 255, 0.04);
		border-color: #ff5e7e;
	}
	
	:global([data-theme='dark']) .related-article__tag {
		background: rgba(255, 255, 255, 0.06);
		color: rgba(255, 255, 255, 0.5);
	}
</style>